import Raven from "raven-js";
import { getCurrentClan } from "helpers/currentAccount";
import settings from "settings";
import { ATTRIBUTES_VALIDATION_STATE } from "fe2/Components/UIKit/ClanCreate/Constants";
import { delay } from "helpers/delay";
import { getShiftedIsoString } from "helpers/datetime";
import { fetchWrapper as fetch } from "helpers/fetch";
import { t } from "helpers/localization";
import * as web2ClientAPI from "web2ClientAPI/web2ClientAPI";

import { updateAccountInfo, decreaseAccountTotalGold } from "./ActionAccount";

import { changeClanAttributesSuccessed, processError, shouldHideDialogOnError } from "./ActionClanEdit";

export const ERROR_RENAMING_CLAN = 'ERROR_RENAMING_CLAN';
export const FINISH_CHECK_AVAILABILITY_CLAN_RENAMING = 'FINISH_CHECK_AVAILABILITY_CLAN_RENAMING';
export const FINISH_CHECK_CLAN_RENAMING_STATUS = 'FINISH_CHECK_CLAN_RENAMING_STATUS';
export const SET_INITIAL_STATE_RENAMING_CLAN = 'SET_INITIAL_STATE_RENAMING_CLAN';
export const SET_STATUS_URL_RENAMING_CLAN = 'SET_STATUS_URL_RENAMING_CLAN';
export const START_CHECK_AVAILABILITY_CLAN_RENAMING = 'START_CHECK_AVAILABILITY_CLAN_RENAMING';
export const START_CHECK_CLAN_RENAMING_STATUS = 'START_CHECK_CLAN_RENAMING_STATUS';
export const START_RENAMING_CLAN = 'START_RENAMING_CLAN';
export const STOP_RENAMING_CLAN = 'STOP_RENAMING_CLAN';
export const UPDATE_RENAMING_CLAN_FIELD_VALUE = 'UPDATE_RENAMING_CLAN_FIELD_VALUE';

export const startRenamingClan = () => {
  return {
    type: START_RENAMING_CLAN
  };
};

export const stopRenamingClan = () => {
  return {
    type: STOP_RENAMING_CLAN
  };
};

export const errorRenamingClan = error => {
  return {
    type: ERROR_RENAMING_CLAN,
    error
  };
};

export const updateFieldValueRenamingClan = (field, value, error) => {
  return {
    type: UPDATE_RENAMING_CLAN_FIELD_VALUE,
    field,
    value,
    error
  };
};

export const setStatusUrlRenamingClan = url => {
  return {
    type: SET_STATUS_URL_RENAMING_CLAN,
    url
  };
};

export const startCheckAvailabilityRenamingClan = field => {
  return {
    type: START_CHECK_AVAILABILITY_CLAN_RENAMING,
    field
  };
};

export const finishCheckAvailabilityRenamingClan = data => {
  return {
    type: FINISH_CHECK_AVAILABILITY_CLAN_RENAMING,
    data
  };
};

export const setInitialStateRenamingClan = () => {
  return {
    type: SET_INITIAL_STATE_RENAMING_CLAN
  };
};

export const startCheckingRenamingClanStatus = () => {
  return {
    type: START_CHECK_CLAN_RENAMING_STATUS
  };
};

export const finishCheckingRenamingClanStatus = () => {
  return {
    type: FINISH_CHECK_CLAN_RENAMING_STATUS
  };
};

const checkRenamingClanStatus = (url, start) => {
  return delay(settings.ajaxTools.pendingOperationWait).then(() => fetch(url)).then(json => {
    if (json.task.status === 'ok' || json.task.status === 'error') {
      return json.task;
    } else if (json.task.status === 'processing') {
      if (start + settings.ajaxTools.pendingOperationTimeout < Date.now()) {
        return { status: 'timeout' };
      }
      return checkRenamingClanStatus(url, start);
    }
  });
};

export const singleCheckRenamingClanStatus = () => {
  return (dispatch, getState) => {
    const state = getState();
    const url = state.clanRename.statusUrl;
    return fetch(url).then(json => {
      if (json.task.status === 'ok') {
        const state = getState();
        const account = state.currentAccount;
        const currentClan = getCurrentClan(state);
        const data = {
          tag: state.clanRename.tag.value || currentClan.tag,
          name: state.clanRename.name.value || currentClan.name
        };
        processSuccessRenameClan(dispatch, account, data, currentClan);
      }
    });
  };
};

const processSuccessRenameClan = (dispatch, account, tagAndNameData, currentClan) => {
  const hasFreeRenameAccess = account.isFreeRenameAvailable && account.roleName === 'commander';
  const renameCost = hasFreeRenameAccess ? 0 : settings.clanRename.cost;
  const accountInfoToUpdate = {
    isFreeRenameAvailable: renameCost ? account.isFreeRenameAvailable : false,
    nextRenameAvailableAt: getShiftedIsoString(settings.clanRename.renameCooldown)
  };

  web2ClientAPI.sendClanRenameCompleteNotification(currentClan, tagAndNameData, renameCost);
  dispatch(errorRenamingClan(null));
  dispatch(decreaseAccountTotalGold(renameCost));
  dispatch(updateAccountInfo(accountInfoToUpdate));
  dispatch(changeClanAttributesSuccessed(Object.assign({}, tagAndNameData, {
    clanId: account.clanId
  })));
  dispatch(setInitialStateRenamingClan());
};

export const renameClan = () => {
  return (dispatch, getState) => {
    /* eslint-disable no-console */
    const state = getState();

    if (state.clanRename.isProcessing) {
      const msg = 'Clan in renaming process';
      console.error(msg);
      if (settings.sentryUrl) {
        Raven.captureMessage(msg, { extra: state.clanRename });
      }
      return {};
    }

    const {
      tag,
      name
    } = state.clanRename;
    const availableState = ATTRIBUTES_VALIDATION_STATE.AVAILABLE;

    if (tag.available !== availableState || name.available !== availableState) {
      console.error('Tag or name is not available');
      return {};
    }

    dispatch(startRenamingClan());

    const account = state.currentAccount;
    const currentClan = getCurrentClan(state);
    const data = {
      tag: tag.value || currentClan.tag,
      name: name.value || currentClan.name
    };

    return fetch(state.urls.renameClan, {
      method: 'POST',
      body: data
    }).then(json => {
      if (json.status === 'processing') {
        dispatch(startCheckingRenamingClanStatus());
        dispatch(setStatusUrlRenamingClan(json.status_url));
        return checkRenamingClanStatus(json.status_url, Date.now());
      }
      return json;
    }).then(json => {
      if (json.status === 'error' || json.errors || !!json.error) {
        processError(dispatch, json, web2ClientAPI.sendClanRenameErrorNotification, currentClan);
        dispatch(errorRenamingClan(json));
        return shouldHideDialogOnError(json);
      } else if (json.status === 'timeout') {
        dispatch(finishCheckingRenamingClanStatus());
      } else {
        processSuccessRenameClan(dispatch, account, data, currentClan);
      }
      return true;
    }, () => {
      web2ClientAPI.sendClanRenameErrorNotification();
      dispatch(errorRenamingClan({ error: t('Произошла ошибка. Повторите попытку позже') }));
      return false;
    });
  };
};